/* eslint-disable max-classes-per-file */
import { Injectable } from "@angular/core";
import { Anskaffningsformer, AvverkningsgruppForkortning, Planeringsstatus, Produktionsstatus } from "~app/common/models/constants";
import { IArbetsorder } from "~app/features/planera/model/IArbetsorder";
import { IOperation } from "~app/features/planera/model/IOperation";

@Injectable({
    providedIn: "root"
})
export class ArbetsorderKartSymbolService {

    private static readonly iconColorRed = "#FFA6A6";
    private static readonly iconColorGrey = "#969696";
    private static readonly iconColorBlue = "#72C9FF";
    private static readonly iconColorYellow = "#FCE683";
    private static readonly iconColorWhite = "#FFFFFF";
    private static readonly iconColorGreen = "#008000";
    private static readonly iconColorPurple = "#E2CCFF";
    private static readonly iconColorUndefined = "#000000";

    private static readonly iconColors: string[] = [
        ArbetsorderKartSymbolService.iconColorRed,
        ArbetsorderKartSymbolService.iconColorBlue,
        ArbetsorderKartSymbolService.iconColorGrey,
        ArbetsorderKartSymbolService.iconColorYellow,
        ArbetsorderKartSymbolService.iconColorWhite,
        ArbetsorderKartSymbolService.iconColorGreen,
        ArbetsorderKartSymbolService.iconColorPurple,
        ArbetsorderKartSymbolService.iconColorUndefined
    ];

    private static readonly typDictionary: { [key: string]: string } = {
        FA: "FA",
        GA: "GA",
        EN: "EN",
        GR: "GR",
        KO: "KO",
        LV: "LV",
        OA: "ÖA",
        NS: "NS",
        HF: "HF",
        XX: "XX",
        IM: "IM",
        ET: "ET",
        CK: "CK"
    };

    private static iconShapes: ArbetsorderKartSymbolShape[];

    private static icons: ArbetsorderKartInfoSymbol[];

    private static iconsWithText: ArbetsorderKartInfoSymbol[];

    constructor() {
        ArbetsorderKartSymbolService.iconShapes = ArbetsorderKartSymbolService.generateShapes();
        ArbetsorderKartSymbolService.icons = ArbetsorderKartSymbolService.generateIcons();
        ArbetsorderKartSymbolService.iconsWithText = ArbetsorderKartSymbolService.generateIconsWithText();
    }

    private static generateShapes(): ArbetsorderKartSymbolShape[] {
        return [
            {
                shape: "CIRCLE",
                symbolSize: 22,
                arcgisStyle: "circle",
                svgPath: undefined
            },
            {
                shape: "SQUARE",
                symbolSize: 21,
                arcgisStyle: "path",
                svgPath: "M3 8C3 6.37 4.37 5 6 5H23C24.63 5 26 6.37 26 8V25C26 26.63 24.63 28 23 28H6C4.37 28 3 26.63 3 25V8Z"
            },
            {
                shape: "TRIANGLE",
                symbolSize: 30,
                arcgisStyle: "path",

                svgPath: "M2.182 17.121 13.323 2.678C14.335 1.366 16.218 1.123 17.53 2.135 17.734 2.292 17.916 2.474 18.074 2.678L29.214 17.121C30.226 18.433 29.982 20.317 28.671 21.329 28.146 21.734 27.501 21.953 26.838 21.953L15.698 21.953 4.558 21.953C2.901 21.953 1.558 20.61 1.558 18.953 1.558 18.29 1.777 17.646 2.182 17.121Z"
            }
        ];
    }

    private static generateIconsWithText(): ArbetsorderKartInfoSymbol[] {
        const allIcons: ArbetsorderKartInfoSymbol[] = [];
        ArbetsorderKartSymbolService.iconShapes.forEach(shape => {
            ArbetsorderKartSymbolService.iconColors.forEach(color => {
                for (const [key, text] of Object.entries(ArbetsorderKartSymbolService.typDictionary)) {
                    allIcons.push(new ArbetsorderKartInfoSymbol({
                        id: undefined,
                        shape: shape.shape,
                        svgPath: shape.svgPath,
                        schemaColor: undefined,
                        iconText: text,
                        iconKey: key,
                        iconFillColor: color,
                        iconLineColor: undefined,
                        symbolSize: shape.symbolSize,
                        arcgisStyle: shape.arcgisStyle
                    }));
                }
            });
        });
        return allIcons;
    }

    private static generateIcons(): ArbetsorderKartInfoSymbol[] {
        const allIcons: ArbetsorderKartInfoSymbol[] = [];
        ArbetsorderKartSymbolService.iconShapes.forEach(shape => {
            ArbetsorderKartSymbolService.iconColors.forEach(color => {
                allIcons.push({
                    ...shape,
                    iconFillColor: color
                } as ArbetsorderKartInfoSymbol);
            });
        });
        return allIcons;
    }

    getSchemaInfoForArbetsorder(operation: IOperation) {
        return this.getSchemaColor(operation);
    }

    getIconInfoForArbetsorder(ao: IArbetsorder): ArbetsorderKartInfoSymbol {
        const key = this.getForkortning(ao);
        const text = ArbetsorderKartSymbolService.typDictionary[key];
        return new ArbetsorderKartInfoSymbol({
            ...this.getShape(ao),
            iconText: text,
            iconKey: key,
            iconFillColor: this.getIconFillColor(ao),
            iconLineColor: "#666"
        });
    }
    getAllIcons() {
        return ArbetsorderKartSymbolService.icons;
    }
    getAllIconsWithText() {
        return ArbetsorderKartSymbolService.iconsWithText;
    }

    private getForkortning(ao: IArbetsorder): string {
        if (ao.anskaffningsform === Anskaffningsformer.EgenTerminal) {
            return "ET";
        } else if (ao.anskaffningsform === Anskaffningsformer.Import) {
            return "IM";
        } else if (ao.anskaffningsform === Anskaffningsformer.CentralaKop) {
            return "CK";
        } else {
            switch (ao.avverkningsgruppForkortning) {
                case AvverkningsgruppForkortning.Foryngringsavverkning:
                    return "FA";
                case AvverkningsgruppForkortning.Gallring:
                    return "GA";
                case AvverkningsgruppForkortning.Energived:
                    return "EN";
                case AvverkningsgruppForkortning.Grot:
                    return "GR";
                case AvverkningsgruppForkortning.Kombinerad:
                    return "KO";
                case AvverkningsgruppForkortning.Leveransvirke:
                    return "LV";
                case AvverkningsgruppForkortning.OvrigAvverkning:
                    return "OA";
                case AvverkningsgruppForkortning.NSAvverkning:
                    return "NS";
                case AvverkningsgruppForkortning.HyggesfriAvverkning:
                    return "HF";
                default:
                    return "XX";
            }
        }
    }

    private getIconFillColor(ao: IArbetsorder): string {
        if (this.IsArbetsorderstatusProduktionGodkand(ao)) { //Produktion godkänd
            return ArbetsorderKartSymbolService.iconColorPurple; // "#E2CCFF"; // Lila
        } else if (this.isProduktionsstatusProduktionSlutrapporterad(ao)) {
            return ArbetsorderKartSymbolService.iconColorGreen; // "#008000"; // Grön
        } else if (this.isProduktionsstatusProduktionAvslutad(ao)) {
            return ArbetsorderKartSymbolService.iconColorGrey; // "#969696"; // Grå
        } else if (this.isTillganglig(ao) && this.isProduktionsstatusProduktionEjPaborjad(ao) && this.isSchemlagd(ao)) {
            return ArbetsorderKartSymbolService.iconColorYellow; // "#FCE683"; // Gul
        } else if (this.isTillganglig(ao) && this.isProduktionsstatusProduktionEjPaborjad(ao) && !this.isSchemlagd(ao)) {
            return ArbetsorderKartSymbolService.iconColorWhite; // "#FFFFFF"; // Vit
        } else if (this.isProduktionsstatusProduktionPagar(ao) && this.isSchemlagd(ao)) {
            return ArbetsorderKartSymbolService.iconColorBlue; // "#72C9FF"; // Blå
        } else if (!this.isTillganglig(ao)) {
            return ArbetsorderKartSymbolService.iconColorRed; // "#FFA6A6"; // Röd
        } else {
            return ArbetsorderKartSymbolService.iconColorUndefined; // "#000";
        }
    }

    private getSchemaColor(o: IOperation): string {
        if (o.arbetsorderProduktionsstatus === Produktionsstatus.ProduktionGodkand) {
            return "#E2CCFF"; // Lila
        } else if (o.slutrapporteradDatum != null) {
            return "#C5D8C5"; // Ljusgrön
        } else if (o.skotningAvslutadDatum !== null) {
            return "#DADADA"; // Grå
        } else if (((o.skordningPaborjadDatum !== null || o.skotningPaborjadDatum !== null) && o.skotningAvslutadDatum === null)) {
            return "#CBEBFE"; // Blå
        } else if (o.arbetsorderPlaneringStatus === Planeringsstatus.Tillgangligt) {
            return "#FFF8D8"; // Gul
        } else if (o.arbetsorderPlaneringStatus !== Planeringsstatus.Tillgangligt) {
            return "#FCCDCD"; // Röd
        } else {
            return "#000";
        }
    }

    private isSchemlagd(ao: IArbetsorder) {
        // Om periodsattperiod är null och det finns operationer så är det schemalagt
        // OBS: Kan vara potentiell bugg här om det är så att man måste kolla av om det är en specifik operation
        return (ao.periodsattPeriod === null && ao.operationer.length > 0);
    }

    private isTillganglig(ao: IArbetsorder): boolean {
        return (ao.planeringsstatus === Planeringsstatus.Tillgangligt);
    }

    private isProduktionsstatusProduktionEjPaborjad(ao: IArbetsorder): boolean {
        return (ao.tidpunkter.skordningPaborjadDatum === null && ao.tidpunkter.skotningPaborjadDatum === null && ao.tidpunkter.skotningAvslutadDatum === null);
    }

    private isProduktionsstatusProduktionPagar(ao: IArbetsorder): boolean {
        return ((ao.tidpunkter.skordningPaborjadDatum !== null || ao.tidpunkter.skotningPaborjadDatum !== null) && ao.tidpunkter.skotningAvslutadDatum === null);
    }

    private isProduktionsstatusProduktionAvslutad(ao: IArbetsorder): boolean {
        return (ao.tidpunkter.skotningAvslutadDatum !== null || (ao as any).produktionsstatus === Produktionsstatus.ProduktionAvslutad);
    }

    private isProduktionsstatusProduktionSlutrapporterad(ao: IArbetsorder): boolean {
        return (ao.tidpunkter.slutrapporteradDatum !== null || (ao as any).produktionsstatus === Produktionsstatus.Slutrapporterad);
    }

    private IsArbetsorderstatusProduktionGodkand(ao: IArbetsorder): boolean {
        return (ao.produktionsstatus === Produktionsstatus.ProduktionGodkand);
    }

    private getShape(ao: IArbetsorder) {
        switch (ao.avverkningsgruppForkortning) {
            case AvverkningsgruppForkortning.Foryngringsavverkning:
            case AvverkningsgruppForkortning.Gallring:
            case AvverkningsgruppForkortning.Energived:
            case AvverkningsgruppForkortning.Grot:
            case AvverkningsgruppForkortning.Kombinerad:
            case AvverkningsgruppForkortning.Leveransvirke:
            case AvverkningsgruppForkortning.HyggesfriAvverkning:
                return ArbetsorderKartSymbolService.iconShapes[0];
            case AvverkningsgruppForkortning.OvrigAvverkning:
                return ArbetsorderKartSymbolService.iconShapes[1];
            case AvverkningsgruppForkortning.NSAvverkning:
                return ArbetsorderKartSymbolService.iconShapes[2];
            default:
                return ArbetsorderKartSymbolService.iconShapes[0];
        }
    }
}

export class ArbetsorderKartInfoSymbol {
    id: string;
    shape: string;
    svgPath: string;
    schemaColor: string;
    iconText: string;
    iconKey: string;
    iconFillColor: string;
    iconLineColor: string;
    symbolSize: number;
    arcgisStyle: string;

    constructor(props: ArbetsorderKartInfoSymbolProps) {
        this.id = props.id;
        this.shape = props.shape;
        this.svgPath = props.svgPath;
        this.schemaColor = props.schemaColor;
        this.iconText = props.iconText;
        this.iconKey = props.iconKey;
        this.iconFillColor = props.iconFillColor;
        this.iconLineColor = props.iconLineColor;
        this.symbolSize = props.symbolSize;
        this.arcgisStyle = props.arcgisStyle;
    }

    getImgSrc(): string {
        return `${this.imgPath()}${this.getUniqueId()}.png`;
    }
    getImgSrcSelected(): string {
        return `${this.imgPath()}${this.getUniqueId()}_selected.png`;
    }

    getUniqueId(): string {
        let fileShape = "";
        switch (this.shape) {
            case "CIRCLE":
                fileShape = "Circle";
                break;
            case "TRIANGLE":
                fileShape = "Triangle";
                break;
            case "SQUARE":
                fileShape = "Square";
                break;
        }
        return `ao-icon-${this.iconKey}-${fileShape}-${this.iconFillColor.substring(1, 7)}`;
    }

    private imgPath(): string {
        return "/core/assets/img/ao-icon/";
    }
}

export class ArbetsorderKartSymbolShape {
    shape: string;
    symbolSize: number;
    svgPath: string;
    arcgisStyle: string;
}

type ArbetsorderKartInfoSymbolProps = {
    id?: string;
    shape?: string;
    svgPath?: string;
    schemaColor?: string;
    iconText?: string;
    iconKey?: string;
    iconFillColor?: string;
    iconLineColor?: string;
    symbolSize?: number;
    arcgisStyle?: string;
};

export class ArbetsorderKartSymbolIcon {
    shape: ArbetsorderKartSymbolShape;
    color: string;
}
